import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import Calendar from 'react-calendar';
import api from '../../utils/api';
import { SkeletonList } from '../layout/SkeletonCard';
import Toast from '../layout/Toast';
import Modal from '../layout/Modal';
import { updateCheckout } from '../../actions/checkout';
import { getSpecialistByNombres } from '../../actions/especialistas';
import './Calendar.css';

const hasValidName = (value) => typeof value === 'string' && value.trim() !== '';
const toSpecialistSlug = (nombres) =>
  (typeof nombres === 'string' ? nombres : '').trim().replace(/\s+/g, '-');

const ScheduleByDay = ({ user, updateCheckout, getSpecialistByNombres }) => {
  const history = useHistory();
  const [date, setDate] = useState(new Date(Date.now() + 86400000));
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [toast, setToast] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false); // { time, specialistId }

  const getScheduleForDate = async (selectedDate) => {
    setLoading(true);
    setError('');
    setSchedule([]);
    setSelectedSlot(null);
    try {
      const dateString = selectedDate.toISOString().split('T')[0];
      const res = await api.get(`/available?date=${dateString}`);
      setSchedule(res.data);
      if (res.data.length > 0 && !availableDates.includes(dateString)) {
        setAvailableDates([...availableDates, dateString]);
      }
    } catch (err) {
      console.error(err);
      setError('No se pudo cargar la disponibilidad. Por favor, intente de nuevo.');
    }
    setLoading(false);
  };

  const tileClassName = ({ date, view }) => {
    if (view === 'month') {
      const dateString = date.toISOString().split('T')[0];
      return availableDates.includes(dateString) ? 'has-availability' : null;
    }
  };

  useEffect(() => {
    getScheduleForDate(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const onDateChange = (newDate) => {
    setDate(newDate);
  };

  const formatTime = (isoString) => {
    return new Date(isoString).toLocaleTimeString('es-PE', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  const handleSelectSlot = (time, specialistId) => {
    setSelectedSlot({ time, specialistId });
  }

  const handleReservarClick = () => {
    if (!selectedSlot) {
      setToast({ message: 'Por favor selecciona un terapeuta y horario', type: 'warning' });
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmReservation = async () => {
    setShowConfirmModal(false);

    try {
      let selectedSpecialist = null;
      for (const slot of schedule) {
        if (slot.time === selectedSlot.time) {
          selectedSpecialist = slot.specialists.find(s => s._id === selectedSlot.specialistId);
          break;
        }
      }

      if (!selectedSpecialist) {
        setToast({ message: 'No se encontró el especialista seleccionado', type: 'error' });
        return;
      }

      if (!hasValidName(selectedSpecialist.nombres)) {
        setToast({ message: 'El terapeuta seleccionado no tiene nombre válido', type: 'error' });
        return;
      }

      await getSpecialistByNombres(selectedSpecialist.nombres);

      updateCheckout({
        cliente: user._id,
        newSession: true,
        especialista: selectedSpecialist._id,
        tarifa: selectedSpecialist.tarifas,
        horaPactada: new Date(selectedSlot.time),
        reagendo: false,
      });

      history.push('/casiacabamos');
    } catch (err) {
      console.error('Error al reservar:', err);
      setToast({ message: 'Hubo un error al procesar tu reserva. Por favor, intenta de nuevo.', type: 'error' });
    }
  };

  const getSelectedSpecialist = () => {
    if (!selectedSlot) return null;
    for (const slot of schedule) {
      if (slot.time === selectedSlot.time) {
        return slot.specialists.find(s => s._id === selectedSlot.specialistId);
      }
    }
    return null;
  };

  return (
    <div className='container mx-auto px-8 pt-8 pb-24'>
      <h1 className='text-3xl font-bold text-center mb-3 text-gray-900'>Agenda tu Cita</h1>
      <p className='text-center mb-8 text-gray-600'>Selecciona un día para ver todos los horarios y terapeutas disponibles</p>

      <div className='flex flex-col md:flex-row md:space-x-8'>
        <div className='md:w-1/3 flex justify-center mb-6 md:mb-0'>
          <Calendar
            onChange={onDateChange}
            value={date}
            minDate={new Date()}
            locale="es-ES"
            tileClassName={tileClassName}
          />
        </div>

        <div className='md:w-2/3'>
          <h2 className='text-2xl font-bold mb-6 text-gray-900'>
            {date.toLocaleDateString('es-ES', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
          </h2>

          {loading ? (
            <SkeletonList count={4} />
          ) : error ? (
            <div className='text-red-500 bg-red-100 p-4 rounded'>{error}</div>
          ) : schedule.length > 0 ? (
            <>
              <div className="space-y-6 mb-8">
                {schedule.map(({ time, specialists }) => (
                  <div key={time} className="mb-6">
                    <h3 className="text-lg font-bold text-gray-800 bg-gray-100 p-3 rounded-lg mb-4">
                      {formatTime(time)}
                    </h3>
                    <div className="space-y-3">
                      {specialists.map(specialist => {
                        const isSelected = selectedSlot?.time === time && selectedSlot?.specialistId === specialist._id;
                        const specialistUrl = `/terapeutas/${toSpecialistSlug(specialist.nombres)}`;
                        return (
                          <div
                            key={specialist._id}
                            className={`bg-white p-5 rounded-xl shadow-md transition-all ${
                              isSelected ? 'ring-2 ring-gray-800 border-2 border-gray-800 shadow-lg' : 'hover:shadow-lg border border-gray-200'
                            }`}
                          >
                            <div className="flex items-start">
                              {/* Radio button */}
                              <div
                                className="mr-4 mt-1 cursor-pointer"
                                onClick={() => handleSelectSlot(time, specialist._id)}
                              >
                                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                  isSelected ? 'border-gray-800 bg-gray-50' : 'border-gray-400'
                                }`}>
                                  {isSelected && (
                                    <div className="w-3.5 h-3.5 rounded-full bg-gray-800"></div>
                                  )}
                                </div>
                              </div>

                              {/* Specialist info */}
                              <div className="flex items-start flex-1">
                                <Link to={specialistUrl}>
                                  <img
                                    className="h-20 w-20 object-cover rounded-full cursor-pointer hover:opacity-90 transition border-2 border-gray-200"
                                    src={specialist.avatar || '/default-avatar.png'}
                                    alt="Foto de Perfil"
                                    onError={(e) => {
                                      e.target.src = '/default-avatar.png';
                                    }}
                                  />
                                </Link>
                                <div className="ml-4 flex-1">
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <h4 className="text-lg font-bold text-gray-900">{specialist.nombres}</h4>
                                      {specialist.especialidad && (
                                        <p className="text-xs text-gray-500 mt-0.5">{specialist.especialidad}</p>
                                      )}
                                    </div>
                                    {specialist.numSes && (
                                      <div className="flex flex-col items-center text-higia-cyan ml-3">
                                        <svg
                                          className="h-4 w-4 fill-current"
                                          viewBox="0 0 32 29.6"
                                        >
                                          <path d="M23.6,0c-3.4,0-6.3,2.7-7.6,5.6C14.7,2.7,11.8,0,8.4,0C3.8,0,0,3.8,0,8.4c0,9.4,9.5,11.9,16,21.2c6.1-9.3,16-12.1,16-21.2C32,3.8,28.2,0,23.6,0z" />
                                        </svg>
                                        <div className="text-gray-800 text-xs font-semibold">{specialist.numSes}</div>
                                      </div>
                                    )}
                                  </div>
                                  {specialist.longDesc && (
                                    <p className="text-sm text-gray-600 mt-2 line-clamp-2 leading-relaxed">{specialist.longDesc}</p>
                                  )}
                                  <div className="flex items-center justify-between mt-3">
                                    {specialist.tarifas && Object.keys(specialist.tarifas).length > 0 && (
                                      <div className="text-sm">
                                        {Object.keys(specialist.tarifas).map((serv, idx) => (
                                          <span key={serv} className="text-gray-900 font-bold">
                                            {idx > 0 && ' • '}
                                            S/{specialist.tarifas[serv]}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                    <Link
                                      to={specialistUrl}
                                      className="text-xs text-gray-600 hover:text-gray-900 font-semibold underline transition"
                                    >
                                      Más info
                                    </Link>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Fixed button at bottom */}
              <div className="sticky bottom-0 bg-white p-4 border-t border-gray-200 shadow-xl">
                <button
                  onClick={handleReservarClick}
                  disabled={!selectedSlot}
                  className={`w-full py-4 px-6 rounded-xl font-bold text-lg transition-all transform ${
                    selectedSlot
                      ? 'bg-gray-900 text-white hover:bg-gray-800 shadow-lg hover:shadow-xl hover:-translate-y-0.5'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {selectedSlot ? 'Confirmar y Reservar Cita' : 'Selecciona un terapeuta y horario'}
                </button>
              </div>
            </>
          ) : (
            <div className='bg-gray-50 p-6 rounded-lg text-center'>
              <p className='text-gray-700'>No hay horarios disponibles para este día.</p>
              <p className='text-gray-500 text-sm'>Por favor, selecciona otra fecha.</p>
            </div>
          )}
        </div>
      </div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmReservation}
        title="Confirmar Reserva"
        confirmText="Confirmar Reserva"
        cancelText="Cancelar"
      >
        {selectedSlot && getSelectedSpecialist() && (
          <div className="space-y-3">
            <div className="flex items-center pb-3 border-b">
              <img
                className="h-16 w-16 object-cover rounded-full mr-4"
                src={getSelectedSpecialist().avatar || '/default-avatar.png'}
                alt="Terapeuta"
              />
              <div>
                <p className="font-bold text-lg">{getSelectedSpecialist().nombres}</p>
                {getSelectedSpecialist().especialidad && (
                  <p className="text-sm text-gray-600">{getSelectedSpecialist().especialidad}</p>
                )}
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-600">Fecha y hora</p>
              <p className="font-semibold">{new Date(selectedSlot.time).toLocaleString('es-PE', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
              })}</p>
            </div>
            {getSelectedSpecialist().tarifas && (
              <div>
                <p className="text-sm text-gray-600">Tarifa</p>
                <p className="font-semibold text-higia-bermuda">
                  {Object.keys(getSelectedSpecialist().tarifas).map((serv, idx) => (
                    <span key={serv}>
                      {idx > 0 && ' • '}
                      S/{getSelectedSpecialist().tarifas[serv]}
                    </span>
                  ))}
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.auth.user,
});

export default connect(mapStateToProps, {
  updateCheckout,
  getSpecialistByNombres
})(ScheduleByDay);
